/**
 * useMQTT.js
 * Custom React hook — connects to HiveMQ public broker via WebSocket,
 * subscribes to attendance/rfid, and delivers parsed attendance records.
 */

import { useEffect, useRef, useState, useCallback } from "react";
import mqtt from "mqtt";
import toast from "react-hot-toast";

// ── MQTT broker settings (no auth) ──────────────────────────
const BROKER_URL   = "wss://broker.hivemq.com:8884/mqtt";
const TOPIC_RFID   = "attendance/rfid";

// ── Duplicate-scan guard on frontend (ms) ───────────────────
const DEBOUNCE_MS  = 5000;

export function useMQTT() {
  const [records, setRecords]     = useState([]);   // attendance log
  const [connected, setConnected] = useState(false);
  const clientRef  = useRef(null);
  const lastScanRef = useRef({});   // { uid: timestamp }

  // ── Connect ───────────────────────────────────────────────
  const connect = useCallback(() => {
    // Generate unique client ID to avoid HiveMQ conflicts
    const clientId = "rfid-dashboard-" + Math.random().toString(16).slice(2, 10);

    const client = mqtt.connect(BROKER_URL, {
      clientId,
      clean: true,
      reconnectPeriod: 3000,
      connectTimeout: 10000,
      // No username / password for public broker
    });

    client.on("connect", () => {
      console.log("[MQTT] Connected to broker");
      setConnected(true);
      client.subscribe(TOPIC_RFID, { qos: 0 }, (err) => {
        if (err) console.error("[MQTT] Subscribe error:", err);
        else console.log("[MQTT] Subscribed to:", TOPIC_RFID);
      });
    });

    client.on("message", (topic, payload) => {
      if (topic !== TOPIC_RFID) return;
      try {
        const data = JSON.parse(payload.toString());

        // Frontend duplicate-scan guard
        const now = Date.now();
        const uid = data.uid || "unknown";
        if (
          lastScanRef.current[uid] &&
          now - lastScanRef.current[uid] < DEBOUNCE_MS
        ) {
          console.log("[MQTT] Duplicate scan ignored:", uid);
          return;
        }
        lastScanRef.current[uid] = now;

        // Normalize record
        const record = {
          id:        now + "-" + uid,
          uid:       uid.toUpperCase(),
          timestamp: data.timestamp || new Date().toLocaleString(),
          status:    (data.status || "present").toLowerCase(),
        };

        // Prepend so latest is always on top
        setRecords((prev) => [record, ...prev]);

        // Toast notification
        if (record.status === "present") {
          toast.success(`Card scanned: ${record.uid}`, {
            icon: "🟢",
            duration: 3000,
          });
        } else {
          toast.error(`Access denied: ${record.uid}`, {
            icon: "🔴",
            duration: 3000,
          });
        }
      } catch (e) {
        console.error("[MQTT] Invalid JSON payload:", e);
      }
    });

    client.on("reconnect", () => {
      console.log("[MQTT] Reconnecting...");
      setConnected(false);
    });

    client.on("error", (err) => {
      console.error("[MQTT] Error:", err);
      setConnected(false);
    });

    client.on("close", () => {
      console.log("[MQTT] Connection closed");
      setConnected(false);
    });

    clientRef.current = client;
  }, []);

  // ── Mount / unmount ───────────────────────────────────────
  useEffect(() => {
    connect();
    return () => {
      if (clientRef.current) {
        clientRef.current.end(true);
      }
    };
  }, [connect]);

  // ── Clear all records ─────────────────────────────────────
  const clearRecords = useCallback(() => {
    setRecords([]);
    toast("Attendance cleared", { icon: "🗑️" });
  }, []);

  return { records, connected, clearRecords };
}
